import type { ComponentType } from "react"
import type { HostRescheduleOptions } from "../../api/rescheduleApi"
import type { BookingDetails, BookingFormData, BookingStatusView } from "../../bookingCalendarApi"
import { isLiveBooking } from "../../lib/dayGrid"

/** Matches the server's cap on a note or reason — see StaffCancelBookingRequest. */
export const MAX_NOTE_LENGTH = 500

/**
 * Which of the dialog's writes is armed. Each id is one action in `registry.ts`; "reading" is the
 * dialog with none armed. "release" is the affected-appointments list's own (AB#769) and is not in the
 * registry: the appointment dialog never offers it.
 */
export type BookingActionId =
  | "cancelling"
  | "reassigning"
  | "updatingStatus"
  | "rescheduling"
  | "adminRescheduling"
  | "changingType"
  | "linkingReschedule"
  | "release"

export type DialogMode = "reading" | BookingActionId

/** Everything an action may read about the booking the dialog is showing, and the person looking. */
export interface BookingActionContext {
  booking: BookingDetails
  uid: string
  timeZone: string
  /** Holds `appointments:manage`. */
  canManage: boolean
  /** Cal's start has passed. */
  hasStarted: boolean
  /** What the applicant filled in, from CMS. Absent for a booking CMS has no row for. */
  formData?: BookingFormData
  /** The CMS booking request, when there is one. */
  requestId?: string
  /** Where the booking stands in CMS, and what the signed-in person may move it to. */
  statusView?: BookingStatusView
  /** When `statusView` was fetched (epoch ms), so a list fetched before the start can be told stale. */
  statusFetchedAt?: number
  /** Whether the signed-in person may host-reschedule this booking — fetched only when it could be offered. */
  rescheduleOptions?: HostRescheduleOptions
  attendeeName?: string
  /** The attendee's email is the placeholder CMS invents — see `isPlaceholderEmail`. */
  emailIsPlaceholder: boolean
  /**
   * The notes a release starts with (AB#769): set only by a list that offers releasing the slot, which
   * is what makes the release action available.
   */
  releaseNotes?: ReleaseNotes
}

/** The two cancellation notes a release fills in for staff to check before sending. */
export interface ReleaseNotes {
  /** Staff only. */
  internalNote: string
  /** Sent to the customer in CMS's cancellation message. */
  externalNote: string
}

/** How an armed write ended, for a list that shows each row's result (AB#769). */
export type BookingActionResultKind = "done" | "released" | "cancelled" | "reassigned" | "failed"

export interface BookingActionResult {
  kind: BookingActionResultKind
  /** What went wrong, for a failure: the same words the form showed. */
  detail?: string
}

/** What an armed action's form is given by the dialog. */
export interface BookingActionFormProps {
  ctx: BookingActionContext
  /** Disarms the action: closes its dialog, back to the booking. */
  onDone: () => void
  /** Closes the dialog. */
  onClose: () => void
  /** Reports whether the action's write is in flight, so the dialog will not close under it. */
  onBusyChange: (busy: boolean) => void
  /**
   * Told how the write ended — on success, and on a failure the form reports (it shows the error too)
   * — so a list can mark the row. Optional: the appointment dialog does not use it.
   */
  onResult?: (result: BookingActionResult) => void
}

/**
 * One of the appointment dialog's writes. The dialog draws a trigger for every available action while
 * reading; arming one opens its `Form` in a dialog of its own, stacked over the appointment's with the
 * booking still showing behind. The form owns its own state, its fields, its error and the footer
 * that submits it; `onDone` closes that dialog, back to the booking.
 */
export interface BookingAction {
  id: BookingActionId
  /** The trigger's label. */
  label: string
  triggerVariant: "destructive" | "outline"
  triggerClassName?: string
  isAvailable: (ctx: BookingActionContext) => boolean
  /** Why the trigger is drawn but disabled (shown as its tooltip); undefined when usable. */
  disabledReason?: (ctx: BookingActionContext) => string | undefined
  Form: ComponentType<BookingActionFormProps>
}

/**
 * Whether the appointment can still be changed in Cal by the person looking: they administer
 * appointments, Cal still has it live, and it has not started.
 *
 * Nothing to cancel on one Cal already has as cancelled. The grid does not draw those, but the dialog
 * can outlive the refetch that removes one, and an armed button on it would only 502. Nor on one that
 * has started: the server refuses that as well, because a started appointment is given an outcome
 * instead — and moving it would mean booking its replacement in the past.
 */
export function isChangeable(ctx: BookingActionContext): boolean {
  return ctx.canManage && isLiveBooking(ctx.booking) && !ctx.hasStarted
}
